import React from 'react';
import { Grid, Button } from '@mui/material';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { ReactSelect } from 'components/form/field/react_select';
import withStyles from '@mui/styles/withStyles';
import { ApplicationManagerClient } from 'clients/application-manager-client';
import { IGetCustomerprofilePharmacyResponse } from 'models/application-manager/customer-profile/IGetCustomerProfilePharamacyResponse';
import { UserUtils } from 'utils/user-utils';
import { UserPermissions } from 'interfaces/user/Permissions';
import { useDebounce } from 'use-debounce';
import { SearchField } from '../components/search-field';
import { SectionTitleStyled } from '../components/section-title';
import { DataTable } from './table/index';
import { cpStyle } from './index.styles';

const CustomerProfile = (props: any): JSX.Element => {
  const { classes } = props;
  const searchFieldPlaceholder = 'Search';
  const initialSearchTerm = '';
  const debounceTimeMs = 500;

  const [searchTerm, setSearchTerm] = React.useState<string>(initialSearchTerm);
  const [customerId, setCustomerId] = React.useState<number>(0);
  const [pharmacyList, setPharmacyList] = React.useState<IGetCustomerprofilePharmacyResponse[]>([]);
  const customers = useTypedSelector(state => state.filters.customers);
  const user = useTypedSelector(state => state.auth.currentUser);
  const stateSelectedCustomerId = useTypedSelector(state => state.filters.selectedCustomerId);
  const [reloadTrigger, setReloadTrigger] = React.useState<Date | undefined>(undefined);
  const DataTableContext = React.createContext(pharmacyList);
  const [debouncedSearchTerm] = useDebounce<string | undefined>(searchTerm, debounceTimeMs);

  const allCustomers = React.useMemo(() => {
    return customers
      .filter((c: any) => c.active)
      .map(c => ({
        label: c.name,
        value: c.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [customers]);

  React.useEffect(() => {
    (async () => {
      if (customerId) {
        const result = await ApplicationManagerClient.fetchCustomerProfilePharmacies(customerId);
        if (result?.data && result.data.length) {
          setPharmacyList(result.data);
        }
      }
    })();
  }, [customerId, reloadTrigger]);

  const userHasPermissionToEditCustomerProfileItem = React.useMemo<boolean>(() => {
    return UserUtils.userIsPermitted(
      UserPermissions.ApplicationManagerCustomerProfileEdit,
      stateSelectedCustomerId,
    );
  }, [user, stateSelectedCustomerId]);

  const renderExportButton = () => (
    <Button
      variant="outlined"
      className={classes.exportButton}
      onClick={() => {
        // to be implemented
      }}
    >
      EXPORT .XLS
    </Button>
  );

  return (
    <>
      <SectionTitleStyled
        title="Customer Profile"
        rightSideItems={[
          {
            element: null,
            key: 'add-customer-profile-button',
          },
        ]}
      />
      <Grid container direction="column" spacing={2} className={classes.gridContainer}>
        <Grid container justifyContent="space-between" alignItems="flex-end">
          <Grid container item xs={6}>
            {/* Left Side - Customer Field */}
            <Grid item xs={6}>
              <ReactSelect
                label=""
                name="customer"
                isMulti={false}
                fields={allCustomers}
                fullWidth
                handleOnChange={(v: { label: string; value: number }) => {
                  setCustomerId(v.value);
                }}
              />
            </Grid>
            <Grid item xs={6} className={classes.searchField}>
              <Grid container>
                <Grid item xs="auto">
                  <Grid container spacing={0}>
                    <Grid item xs="auto">
                      <SearchField
                        width={48}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        initialValue={initialSearchTerm}
                        placeholder={searchFieldPlaceholder}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {/* Right Side - Export button */}
          <Grid>{renderExportButton()}</Grid>
        </Grid>
        {/* data table here */}
        <DataTableContext.Provider value={pharmacyList}>
          <DataTable
            searchTerm={debouncedSearchTerm}
            pharmacyList={pharmacyList}
            userHasPermissionToEditCustomerProfileItem={userHasPermissionToEditCustomerProfileItem}
            activeAnyField={searchTerm.length > 0}
            setReloadTrigger={setReloadTrigger}
            allCustomers={allCustomers}
            customerId={customerId}
          />
        </DataTableContext.Provider>
      </Grid>
    </>
  );
};
export default withStyles(cpStyle)(CustomerProfile);
